import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  APICommercialForm,
  APIBinderPDF,
  APISigning,
  APIApplicationPayment,
} from 'types/api.types';

// Helper function to calculate total with proper decimal precision
const calculateTotal = (
  premium: number,
  companyFees: number,
  brokerFees: number,
  taxes: number,
): number => {
  return Math.round((premium + companyFees + brokerFees + taxes) * 100) / 100;
};

const splitFullName = (
  full: string,
): { first_name: string; last_name: string } => {
  const trimmed = (full || '').trim();
  if (!trimmed) return { first_name: '', last_name: '' };
  const parts = trimmed.split(/\s+/);
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
};

export interface CommercialFormState {
  data: APICommercialForm;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  validationErrors: Record<string, any>;
  isDataSet: boolean;
  hasUnsavedChanges: boolean;
  wipStatus: number | null;
  lineOfBusiness: 'Property & Casualty' | 'Auto' | null;
  premiumConfirmed: boolean;
  binder_pdf: APIBinderPDF | null;
  signing: APISigning | null;
  // Mirrors of fields the shared MethodOfPayment component reads on the slice.
  isDataSaved: boolean;
  missingMandatoryFields: { name: string; section: number }[];
  payments: APIApplicationPayment[];
}

const initialState: CommercialFormState = {
  data: {
    insurance_company: '',
    effective_date: '',
    effective_date_time: '12:01 AM',
    expiry_date: '',
    binder_policy_number: '',
    business_type: 'new',
    premium: 0,
    is_loa: false,
    client_name: '',
    customer_code: '',
    policy_information: {
      insurance_company: '',
      effective_date: '',
      binder_policy_number: '',
      business_type: 'new',
      premium: 0,
      is_loa: false,
      client_name: '',
      customer_code: '',
      broker_csr_code: '',
    },
    submission_information: {
      submission_type: '',
      how_was_coverage_bound: '',
      checklist_questions: {},
      marketing_approval_date: '',
      support_ticket_number: '',
    },
    billing: {
      include_billing: true, // Always true for commercial forms
      payment_schedule: '',
      payment_method: 'agency_bill',
      payment_type: '' as 'credit_card' | 'eft' | '',
      financing_type: 'paid_full',
      signed_contract_attached: false,
      down_payment: 'no',
      down_payment_amount: 0,
      down_payment_type: '',
      province: '',
      business_code: '',
      company_fees: 0,
      broker_fees: 0,
      taxes: 0,
      total: 0,
      payment_date: null,
      payment_day_of_month: null,
      has_down_payment: null,
      down_payment_method: null,
      down_payment_date: null,
    },
    broker_information: {
      producer_code: '',
      broker: '',
      address: '',
      branch: '',
      city: '',
      email: '',
      phone: '',
      postal_code: '',
      province: '',
    },
    disclosure_and_attestation: {
      broker_name: '',
    },
    additional_broker_note: '',
    notes_to_underwriter: '',
    applicant_email: '',
    applicant_address: {
      address: '',
      city: '',
      postal_code: '',
      province: '',
    },
    method_of_payment: {
      type_of_payment_plan: '',
      estimated_policy_premium: '',
      tax: '',
      interest: '',
      interest_amount: '',
      total_estimated_cost: '',
      amt_paid_with_application: '',
      amount_still_due: '',
      no_of_remaining_instalments: '',
      amount_of_each_instalment: '',
      instalment_due_date: '',
      require_billing: false,
    },
    account_holders: {
      first: {
        email: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        address: '',
        city: '',
        province_code: '',
        postal_code: '',
        phone: '',
        is_same_as_applicant: false,
        is_same_as_co_applicant: false,
      },
      second: {
        email: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        address: '',
        city: '',
        province_code: '',
        postal_code: '',
        phone: '',
        is_same_as_co_applicant: false,
      },
    },
    agency_billing: {
      financing_type: null,
      is_signed_contract_attached: false,
      province_code: '',
      business_code: 'AUTO',
      total_premium: '',
      service_fee: '',
      tax: '',
    },
    is_signing_flow: false,
    co_applicants: [{ name: '', email: '' }],
  },
  isLoading: false,
  isSaving: false,
  error: null,
  validationErrors: {},
  isDataSet: false,
  hasUnsavedChanges: false,
  wipStatus: null,
  lineOfBusiness: null,
  premiumConfirmed: false,
  binder_pdf: null,
  signing: null,
  isDataSaved: false,
  missingMandatoryFields: [],
  payments: [],
};

const commercialFormSlice = createSlice({
  name: 'commercialForm',
  initialState,
  reducers: {
    setInsuranceCompany: (state, action: PayloadAction<string>) => {
      state.data.insurance_company = action.payload;
      state.data.policy_information.insurance_company = action.payload;
      state.hasUnsavedChanges = true;
    },
    setEffectiveDate: (state, action: PayloadAction<string>) => {
      state.data.effective_date = action.payload;
      state.data.policy_information.effective_date = action.payload;
      const match = action.payload.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const nextYear = parseInt(match[1], 10) + 1;
        state.data.expiry_date = `${nextYear}-${match[2]}-${match[3]}`;
      }
      state.hasUnsavedChanges = true;
    },
    setEffectiveDateTime: (state, action: PayloadAction<string>) => {
      state.data.effective_date_time = action.payload;
      state.hasUnsavedChanges = true;
    },
    setExpiryDate: (state, action: PayloadAction<string>) => {
      state.data.expiry_date = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBinderPolicyNumber: (state, action: PayloadAction<string>) => {
      state.data.binder_policy_number = action.payload;
      state.data.policy_information.binder_policy_number = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBusinessType: (state, action: PayloadAction<'new' | 'remarket'>) => {
      state.data.business_type = action.payload;
      state.data.policy_information.business_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPremium: (state, action: PayloadAction<number>) => {
      state.data.premium = action.payload;
      state.data.policy_information.premium = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      // Reset premium confirmation if premium drops to or below threshold
      if (action.payload <= 10000) {
        state.premiumConfirmed = false;
      }
      state.hasUnsavedChanges = true;
    },
    setPremiumConfirmed: (state, action: PayloadAction<boolean>) => {
      state.premiumConfirmed = action.payload;
    },
    setIsLoa: (state, action: PayloadAction<'yes' | 'no' | boolean>) => {
      const value =
        action.payload === 'yes'
          ? true
          : action.payload === 'no'
            ? false
            : action.payload;
      state.data.is_loa = value;
      state.data.policy_information.is_loa = value;
      state.hasUnsavedChanges = true;
    },
    setClientName: (state, action: PayloadAction<string>) => {
      state.data.client_name = action.payload;
      state.data.policy_information.client_name = action.payload;
      if (state.data.account_holders?.first?.is_same_as_applicant) {
        const { first_name, last_name } = splitFullName(action.payload);
        state.data.account_holders.first.first_name = first_name;
        state.data.account_holders.first.last_name = last_name;
      }
      state.hasUnsavedChanges = true;
    },
    setCustomerCode: (state, action: PayloadAction<string>) => {
      state.data.customer_code = action.payload;
      state.data.policy_information.customer_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setSubmissionType: (
      state,
      action: PayloadAction<
        'non_portal' | 'portal_phone' | 'irca' | 'fleet' | 'garage' | ''
      >,
    ) => {
      state.data.submission_information.submission_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setCoverageBoundMethod: (state, action: PayloadAction<string>) => {
      state.data.submission_information.how_was_coverage_bound = action.payload;
      state.hasUnsavedChanges = true;
    },
    setMarketingApprovalDate: (state, action: PayloadAction<string>) => {
      state.data.submission_information.marketing_approval_date =
        action.payload;
      state.hasUnsavedChanges = true;
    },
    setMarketingApprovalTicket: (state, action: PayloadAction<string>) => {
      state.data.submission_information.support_ticket_number = action.payload;
      state.hasUnsavedChanges = true;
    },
    setAdditionalBrokerNote: (state, action: PayloadAction<string>) => {
      state.data.additional_broker_note = action.payload;
      state.hasUnsavedChanges = true;
    },
    setNotesToUnderwriter: (state, action: PayloadAction<string>) => {
      state.data.notes_to_underwriter = action.payload;
      state.hasUnsavedChanges = true;
    },
    setApplicantEmail: (state, action: PayloadAction<string>) => {
      state.data.applicant_email = action.payload;
      // Keep first account holder's email aligned with applicant when the
      // holder is configured as "same as applicant", so the signing flow's
      // primary signer email always matches the form's applicant email.
      if (state.data.account_holders?.first?.is_same_as_applicant) {
        state.data.account_holders.first.email = action.payload;
      }
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setApplicantAddressField: (
      state,
      action: PayloadAction<{
        field: 'address' | 'city' | 'postal_code' | 'province';
        value: string;
      }>,
    ) => {
      state.data.applicant_address[action.payload.field] = action.payload.value;
      // Mirror onto first account holder when configured as "same as applicant".
      // Note: applicant_address uses `province`; account holder uses `province_code`.
      if (state.data.account_holders?.first?.is_same_as_applicant) {
        const holderFieldMap: Record<
          'address' | 'city' | 'postal_code' | 'province',
          'address' | 'city' | 'postal_code' | 'province_code'
        > = {
          address: 'address',
          city: 'city',
          postal_code: 'postal_code',
          province: 'province_code',
        };
        const holderField = holderFieldMap[action.payload.field];
        state.data.account_holders.first[holderField] = action.payload.value;
      }
      state.hasUnsavedChanges = true;
    },
    setCoApplicantField: (
      state,
      action: PayloadAction<{
        index: number;
        field: 'name' | 'email';
        value: string;
      }>,
    ) => {
      if (!state.data.co_applicants) {
        state.data.co_applicants = [{ name: '', email: '' }];
      }
      const entry = state.data.co_applicants[action.payload.index];
      if (entry) {
        entry[action.payload.field] = action.payload.value;
        state.hasUnsavedChanges = true;
      }
    },
    addCoApplicant: (state) => {
      if (!state.data.co_applicants) {
        state.data.co_applicants = [{ name: '', email: '' }];
      }
      if (state.data.co_applicants.length < 3) {
        state.data.co_applicants.push({ name: '', email: '' });
        state.hasUnsavedChanges = true;
      }
    },
    removeCoApplicant: (state, action: PayloadAction<number>) => {
      if (!state.data.co_applicants) return;
      if (state.data.co_applicants.length <= 1) return;
      if (action.payload <= 0) return;
      state.data.co_applicants.splice(action.payload, 1);
      state.hasUnsavedChanges = true;
    },
    setBinderPDF: (state, action: PayloadAction<APIBinderPDF | null>) => {
      state.binder_pdf = action.payload;
    },
    setSigning: (state, action: PayloadAction<APISigning | null>) => {
      state.signing = action.payload;
    },
    changeBilling: (
      state,
      action: PayloadAction<{ name: string; value: any }>,
    ) => {
      state.data.billing = {
        ...state.data.billing,
        [action.payload.name]: action.payload.value,
      };

      if (action.payload.name === 'payment_schedule') {
        state.data.billing.payment_date = null;
        state.data.billing.payment_day_of_month = null;
        state.data.billing.payment_method = '';
        state.data.billing.has_down_payment = null;
        state.data.billing.down_payment_method = null;
        state.data.billing.down_payment_amount = 0;
        state.data.billing.down_payment_date = null;
      }

      if (!state.data.billing.has_down_payment) {
        state.data.billing.down_payment_method = null;
        state.data.billing.down_payment_amount = 0;
        state.data.billing.down_payment_date = null;
      }

      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    changeMethodOfPayment: (
      state,
      action: PayloadAction<{ name: string; value: string | number | boolean }>,
    ) => {
      state.data.method_of_payment = {
        ...state.data.method_of_payment,
        [action.payload.name]: action.payload.value,
      };
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    updatePayments: (state, action: PayloadAction<APIApplicationPayment[]>) => {
      state.payments = action.payload;
    },
    changeAccountHolder: (
      state,
      action: PayloadAction<{
        holder: 'first' | 'second';
        name: string;
        value: string | boolean;
      }>,
    ) => {
      if (action.payload.holder === 'first') {
        state.data.account_holders.first = {
          ...state.data.account_holders.first,
          [action.payload.name]: action.payload.value,
        };

        if (
          action.payload.name === 'is_same_as_applicant' &&
          action.payload.value === true
        ) {
          const applicantAddress = state.data.applicant_address;
          const { first_name, last_name } = splitFullName(
            state.data.client_name,
          );
          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            email: state.data.applicant_email,
            first_name,
            middle_name: '',
            last_name,
            address: applicantAddress?.address || '',
            city: applicantAddress?.city || '',
            province_code: applicantAddress?.province || '',
            postal_code: applicantAddress?.postal_code || '',
            is_same_as_co_applicant: false,
          };
        }

        if (
          action.payload.name === 'is_same_as_co_applicant' &&
          action.payload.value === true
        ) {
          const co = state.data.co_applicants?.[0];
          const { first_name, last_name } = splitFullName(co?.name || '');
          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            email: co?.email || '',
            first_name,
            middle_name: '',
            last_name,
            is_same_as_applicant: false,
          };
        }
      } else {
        state.data.account_holders.second = {
          ...state.data.account_holders.second,
          [action.payload.name]: action.payload.value,
        };
      }
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    addMissingMandatoryField: (
      state,
      action: PayloadAction<{ name: string; section: number }>,
    ) => {
      const exists = state.missingMandatoryFields.some(
        (field) => field.name === action.payload.name,
      );
      if (!exists) {
        state.missingMandatoryFields.push(action.payload);
      }
    },
    removeMissingMandatoryField: (state, action: PayloadAction<string>) => {
      state.missingMandatoryFields = state.missingMandatoryFields.filter(
        (field) => field.name !== action.payload,
      );
    },
    changeAgencyBilling: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      if (!state.data.agency_billing) {
        state.data.agency_billing = {
          financing_type: null,
          is_signed_contract_attached: false,
          province_code: '',
          business_code: 'AUTO',
          total_premium: '',
          service_fee: '',
          tax: '',
        };
      }

      if (action.payload.name === 'financing_type') {
        state.data.agency_billing.financing_type = action.payload.value as
          | 'paid_full'
          | 'premium_financing';
      } else if (action.payload.name === 'is_signed_contract_attached') {
        state.data.agency_billing.is_signed_contract_attached = action.payload
          .value as boolean;
      } else if (action.payload.name === 'province_code') {
        state.data.agency_billing.province_code = action.payload
          .value as string;
      } else if (action.payload.name === 'business_code') {
        state.data.agency_billing.business_code = action.payload
          .value as string;
      } else if (action.payload.name === 'total_premium') {
        state.data.agency_billing.total_premium = action.payload
          .value as string;
      } else if (action.payload.name === 'service_fee') {
        state.data.agency_billing.service_fee = action.payload.value as string;
      }

      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    changeIsDataSaved: (state, action: PayloadAction<boolean>) => {
      state.isDataSaved = action.payload;
    },
    setChecklistItem: (
      state,
      action: PayloadAction<{ id: string; checked: boolean }>,
    ) => {
      if (!state.data.submission_information.checklist_questions) {
        state.data.submission_information.checklist_questions = {};
      }
      state.data.submission_information.checklist_questions[action.payload.id] =
        action.payload.checked;
      state.hasUnsavedChanges = true;
    },
    removeChecklistItem: (state, action: PayloadAction<string>) => {
      if (state.data.submission_information.checklist_questions) {
        delete state.data.submission_information.checklist_questions[
          action.payload
        ];
        state.hasUnsavedChanges = true;
      }
    },
    setIncludeBilling: (state, action: PayloadAction<boolean>) => {
      state.data.billing.include_billing = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaymentSchedule: (state, action: PayloadAction<string>) => {
      state.data.billing.payment_schedule = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaymentMethod: (
      state,
      action: PayloadAction<'agency_bill' | 'eft_credit_card'>,
    ) => {
      state.data.billing.payment_method = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBillingProvince: (state, action: PayloadAction<string>) => {
      state.data.billing.province = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBusinessCode: (state, action: PayloadAction<string>) => {
      state.data.billing.business_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setCompanyFees: (state, action: PayloadAction<number>) => {
      state.data.billing.company_fees = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      state.hasUnsavedChanges = true;
    },
    setBrokerFees: (state, action: PayloadAction<number>) => {
      state.data.billing.broker_fees = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      state.hasUnsavedChanges = true;
    },
    setTaxes: (state, action: PayloadAction<number>) => {
      state.data.billing.taxes = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      state.hasUnsavedChanges = true;
    },
    setFinancingType: (
      state,
      action: PayloadAction<'paid_full' | 'premium_financing'>,
    ) => {
      state.data.billing.financing_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setSignedContractAttached: (state, action: PayloadAction<boolean>) => {
      state.data.billing.signed_contract_attached = action.payload;
      state.hasUnsavedChanges = true;
    },
    setDownPayment: (state, action: PayloadAction<'yes' | 'no'>) => {
      state.data.billing.down_payment = action.payload;
      if (action.payload === 'no') {
        state.data.billing.down_payment_amount = 0;
        state.data.billing.down_payment_type = '';
      }
      state.hasUnsavedChanges = true;
    },
    setPaymentType: (
      state,
      action: PayloadAction<'credit_card' | 'eft' | ''>,
    ) => {
      state.data.billing.payment_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setDownPaymentAmount: (state, action: PayloadAction<number>) => {
      state.data.billing.down_payment_amount = action.payload;
      state.hasUnsavedChanges = true;
    },
    setDownPaymentType: (state, action: PayloadAction<string>) => {
      state.data.billing.down_payment_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    setDataSet: (state, action: PayloadAction<boolean>) => {
      state.isDataSet = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
      state.signing = null;
      state.binder_pdf = null;
    },
    setWipStatus: (state, action: PayloadAction<number | null>) => {
      state.wipStatus = action.payload;
    },
    setLineOfBusiness: (
      state,
      action: PayloadAction<'Property & Casualty' | 'Auto' | null>,
    ) => {
      state.lineOfBusiness = action.payload;
    },
    changeDisclosureAndAttestation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      state.data.disclosure_and_attestation = {
        ...state.data.disclosure_and_attestation,
        [action.payload.name]: action.payload.value,
      };
      state.hasUnsavedChanges = true;
    },
    resetForm: (state) => {
      const preservedLineOfBusiness = state.lineOfBusiness;
      return {
        ...initialState,
        lineOfBusiness: preservedLineOfBusiness,
      };
    },
    initializeForm: (
      state,
      action: PayloadAction<Partial<APICommercialForm>>,
    ) => {
      state.data = {
        insurance_company:
          action.payload.insurance_company ||
          action.payload.policy_information?.insurance_company ||
          '',
        effective_date:
          action.payload.effective_date ||
          action.payload.policy_information?.effective_date ||
          '',
        effective_date_time: action.payload.effective_date_time || '12:01 AM',
        expiry_date: action.payload.expiry_date || '',
        binder_policy_number:
          action.payload.binder_policy_number ||
          action.payload.policy_information?.binder_policy_number ||
          '',
        business_type:
          action.payload.business_type ||
          action.payload.policy_information?.business_type ||
          'new',
        premium:
          action.payload.premium ||
          action.payload.policy_information?.premium ||
          0,
        is_loa:
          action.payload.is_loa ??
          action.payload.policy_information?.is_loa ??
          false,
        client_name:
          action.payload.client_name ||
          action.payload.policy_information?.client_name ||
          '',
        customer_code:
          action.payload.customer_code ||
          action.payload.policy_information?.customer_code ||
          '',
        policy_information: {
          insurance_company:
            action.payload.policy_information?.insurance_company ||
            action.payload.insurance_company ||
            '',
          effective_date:
            action.payload.policy_information?.effective_date ||
            action.payload.effective_date ||
            '',
          binder_policy_number:
            action.payload.policy_information?.binder_policy_number ||
            action.payload.binder_policy_number ||
            '',
          business_type:
            action.payload.policy_information?.business_type ||
            action.payload.business_type ||
            'new',
          premium:
            action.payload.policy_information?.premium ||
            action.payload.premium ||
            0,
          is_loa:
            action.payload.policy_information?.is_loa ??
            action.payload.is_loa ??
            false,
          client_name:
            action.payload.policy_information?.client_name ||
            action.payload.client_name ||
            '',
          customer_code:
            action.payload.policy_information?.customer_code ||
            action.payload.customer_code ||
            '',
          broker_csr_code:
            action.payload.policy_information?.broker_csr_code || '',
        },
        submission_information: {
          submission_type:
            action.payload.submission_information?.submission_type || '',
          how_was_coverage_bound:
            action.payload.submission_information?.how_was_coverage_bound || '',
          checklist_questions:
            action.payload.submission_information?.checklist_questions || {},
          marketing_approval_date:
            action.payload.submission_information?.marketing_approval_date ||
            '',
          support_ticket_number:
            action.payload.submission_information?.support_ticket_number || '',
        },
        billing: {
          include_billing: true, // Always set to true for commercial forms
          payment_schedule: action.payload.billing?.payment_schedule || '',
          payment_method:
            action.payload.billing?.payment_method || 'agency_bill',
          financing_type:
            action.payload.billing?.payment_method === 'agency_bill' &&
            !action.payload.billing?.financing_type
              ? 'paid_full'
              : (action.payload.billing?.financing_type as any) || 'paid_full',
          signed_contract_attached:
            action.payload.billing?.signed_contract_attached || false,
          down_payment: action.payload.billing?.down_payment || 'no',
          payment_type: (action.payload.billing as any)?.payment_type || '',
          down_payment_amount:
            (action.payload.billing as any)?.down_payment_amount || 0,
          down_payment_type:
            (action.payload.billing as any)?.down_payment_type || '',
          province: action.payload.billing?.province || '',
          business_code: action.payload.billing?.business_code || '',
          company_fees: action.payload.billing?.company_fees || 0,
          broker_fees: action.payload.billing?.broker_fees || 0,
          taxes: action.payload.billing?.taxes || 0,
          // Recalculate total to fix any floating-point precision errors
          total: calculateTotal(
            action.payload.premium ||
              action.payload.policy_information?.premium ||
              0,
            action.payload.billing?.company_fees || 0,
            action.payload.billing?.broker_fees || 0,
            action.payload.billing?.taxes || 0,
          ),
          payment_date: action.payload.billing?.payment_date ?? null,
          payment_day_of_month:
            action.payload.billing?.payment_day_of_month ?? null,
          has_down_payment: action.payload.billing?.has_down_payment ?? null,
          down_payment_method:
            action.payload.billing?.down_payment_method ?? null,
          down_payment_date: action.payload.billing?.down_payment_date ?? null,
        },
        broker_information: {
          producer_code: action.payload.broker_information?.producer_code || '',
          branch: action.payload.broker_information?.branch || '',
          broker: action.payload.broker_information?.broker || '',
          address: action.payload.broker_information?.address || '',
          city: action.payload.broker_information?.city || '',
          email: action.payload.broker_information?.email || '',
          phone: action.payload.broker_information?.phone || '',
          postal_code: action.payload.broker_information?.postal_code || '',
          province: action.payload.broker_information?.province || '',
        },
        disclosure_and_attestation: {
          broker_name:
            action.payload.disclosure_and_attestation?.broker_name || '',
        },
        additional_broker_note: action.payload.additional_broker_note || '',
        notes_to_underwriter: action.payload.notes_to_underwriter || '',
        applicant_email: action.payload.applicant_email || '',
        applicant_address: {
          address: action.payload.applicant_address?.address || '',
          city: action.payload.applicant_address?.city || '',
          postal_code: action.payload.applicant_address?.postal_code || '',
          province: action.payload.applicant_address?.province || '',
        },
        method_of_payment: {
          type_of_payment_plan:
            action.payload.method_of_payment?.type_of_payment_plan || '',
          estimated_policy_premium:
            action.payload.method_of_payment?.estimated_policy_premium || '',
          tax: action.payload.method_of_payment?.tax || '',
          interest: action.payload.method_of_payment?.interest || '',
          interest_amount:
            action.payload.method_of_payment?.interest_amount || '',
          total_estimated_cost:
            action.payload.method_of_payment?.total_estimated_cost || '',
          amt_paid_with_application:
            action.payload.method_of_payment?.amt_paid_with_application || '',
          amount_still_due:
            action.payload.method_of_payment?.amount_still_due || '',
          no_of_remaining_instalments:
            action.payload.method_of_payment?.no_of_remaining_instalments || '',
          amount_of_each_instalment:
            action.payload.method_of_payment?.amount_of_each_instalment || '',
          instalment_due_date:
            action.payload.method_of_payment?.instalment_due_date || '',
          require_billing:
            action.payload.method_of_payment?.require_billing || false,
        },
        account_holders: {
          first: {
            email: action.payload.account_holders?.first?.email || '',
            first_name: action.payload.account_holders?.first?.first_name || '',
            last_name: action.payload.account_holders?.first?.last_name || '',
            middle_name:
              action.payload.account_holders?.first?.middle_name || '',
            address: action.payload.account_holders?.first?.address || '',
            city: action.payload.account_holders?.first?.city || '',
            province_code:
              action.payload.account_holders?.first?.province_code || '',
            postal_code:
              action.payload.account_holders?.first?.postal_code || '',
            phone: action.payload.account_holders?.first?.phone || '',
            is_same_as_applicant:
              action.payload.account_holders?.first?.is_same_as_applicant ||
              true,
            is_same_as_co_applicant:
              action.payload.account_holders?.first?.is_same_as_co_applicant ||
              false,
          },
          second: {
            email: action.payload.account_holders?.second?.email || '',
            first_name:
              action.payload.account_holders?.second?.first_name || '',
            last_name: action.payload.account_holders?.second?.last_name || '',
            middle_name:
              action.payload.account_holders?.second?.middle_name || '',
            address: action.payload.account_holders?.second?.address || '',
            city: action.payload.account_holders?.second?.city || '',
            province_code:
              action.payload.account_holders?.second?.province_code || '',
            postal_code:
              action.payload.account_holders?.second?.postal_code || '',
            phone: action.payload.account_holders?.second?.phone || '',
            is_same_as_co_applicant:
              action.payload.account_holders?.second?.is_same_as_co_applicant ||
              false,
          },
        },
        agency_billing: {
          financing_type: action.payload.agency_billing?.financing_type ?? null,
          is_signed_contract_attached:
            action.payload.agency_billing?.is_signed_contract_attached || false,
          province_code: action.payload.agency_billing?.province_code || '',
          business_code: action.payload.agency_billing?.business_code || 'AUTO',
          total_premium: action.payload.agency_billing?.total_premium || '',
          service_fee: action.payload.agency_billing?.service_fee || '',
          tax: action.payload.agency_billing?.tax || '',
        },
        is_signing_flow: action.payload.is_signing_flow ?? false,
        co_applicants:
          action.payload.co_applicants &&
          action.payload.co_applicants.length > 0
            ? action.payload.co_applicants.slice(0, 3).map((entry) => ({
                name: entry?.name || '',
                email: entry?.email || '',
              }))
            : [{ name: '', email: '' }],
      };
      state.wipStatus = action.payload.wip_status || null;
      state.premiumConfirmed = action.payload.premium_confirmed || false;
      state.isDataSet = true;
      state.hasUnsavedChanges = false;
      state.isDataSaved = true;
      state.error = null;
      state.validationErrors = {};
      state.binder_pdf = action.payload.binder_pdf || null;
      state.signing = action.payload.signing || null;
      state.payments = action.payload.payments || [];
    },
  },
});

export const {
  setInsuranceCompany,
  setEffectiveDate,
  setEffectiveDateTime,
  setExpiryDate,
  setBinderPolicyNumber,
  setBusinessType,
  setPremium,
  setPremiumConfirmed,
  setIsLoa,
  setClientName,
  setCustomerCode,
  setSubmissionType,
  setCoverageBoundMethod,
  setMarketingApprovalDate,
  setMarketingApprovalTicket,
  setAdditionalBrokerNote,
  setNotesToUnderwriter,
  setApplicantEmail,
  setApplicantAddressField,
  setCoApplicantField,
  addCoApplicant,
  removeCoApplicant,
  setBinderPDF,
  setSigning,
  changeBilling,
  changeMethodOfPayment,
  updatePayments,
  changeAccountHolder,
  addMissingMandatoryField,
  removeMissingMandatoryField,
  changeAgencyBilling,
  changeIsDataSaved,
  setChecklistItem,
  removeChecklistItem,
  setIncludeBilling,
  setPaymentSchedule,
  setPaymentMethod,
  setBillingProvince,
  setBusinessCode,
  setCompanyFees,
  setBrokerFees,
  setTaxes,
  setFinancingType,
  setSignedContractAttached,
  setDownPayment,
  setPaymentType,
  setDownPaymentAmount,
  setDownPaymentType,
  setLoading,
  setSaving,
  setError,
  setValidationErrors,
  clearValidationErrors,
  setDataSet,
  setHasUnsavedChanges,
  setWipStatus,
  setLineOfBusiness,
  changeDisclosureAndAttestation,
  resetForm,
  initializeForm,
} = commercialFormSlice.actions;

export default commercialFormSlice.reducer;
